import React, { useState } from 'react';
import { 
  Settings, 
  Bell, 
  Shield, 
  Eye, 
  Mail, 
  Smartphone, 
  Globe, 
  Moon, 
  Sun, 
  Monitor,
  Save,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../common/Notification';
import LoadingSpinner from '../common/LoadingSpinner';

const UserSettings = () => {
  const { userProfile } = useAuth();
  const { addNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    // Notifications
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    marketingEmails: false,
    
    // Sécurité
    twoFactorAuth: false,
    loginAlerts: true,
    
    // Affichage
    theme: 'system', // light, dark, system
    language: 'fr',
    currency: 'DZD',
    
    // Confidentialité
    profileVisibility: 'private',
    dataSharing: false
  });

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Simuler la sauvegarde des paramètres
      await new Promise(resolve => setTimeout(resolve, 1000));
      addNotification('Paramètres sauvegardés avec succès', 'success');
    } catch (error) {
      addNotification('Erreur lors de la sauvegarde des paramètres', 'error');
    } finally {
      setLoading(false);
    }
  };

  const SettingSection = ({ title, icon: Icon, children }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 sm:mb-6">
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <div className="flex items-center">
          <Icon className="w-5 h-5 text-primary-600 mr-3" />
          <h2 className="text-base sm:text-lg font-semibold text-gray-900">{title}</h2>
        </div>
      </div>
      <div className="p-4 sm:p-6">
        {children}
      </div>
    </div>
  );

  const ToggleSwitch = ({ enabled, onChange, label, description }) => (
    <div className="flex items-start sm:items-center justify-between py-3 gap-4">
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-medium text-gray-900">{label}</h3>
        {description && (
          <p className="text-xs sm:text-sm text-gray-500 mt-1 pr-2">{description}</p>
        )}
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex-shrink-0 ${
          enabled ? 'bg-primary-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  const SelectField = ({ value, onChange, options, label, description }) => (
    <div className="py-3">
      <label className="block text-sm font-medium text-gray-900 mb-1">{label}</label>
      {description && (
        <p className="text-sm text-gray-500 mb-2">{description}</p>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );

  if (!userProfile) {
    return <LoadingSpinner />;
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Paramètres</h1>
        <p className="text-sm sm:text-base text-gray-600">Gérez vos préférences et paramètres de compte</p>
      </div>

      {/* Notifications */}
      <SettingSection title="Notifications" icon={Bell}>
        <div className="space-y-1">
          <ToggleSwitch
            enabled={settings.emailNotifications}
            onChange={(value) => handleSettingChange('emailNotifications', value)}
            label="Notifications par email"
            description="Recevez des notifications importantes par email"
          />
          <ToggleSwitch
            enabled={settings.pushNotifications}
            onChange={(value) => handleSettingChange('pushNotifications', value)}
            label="Notifications push"
            description="Recevez des notifications dans votre navigateur"
          />
          <ToggleSwitch
            enabled={settings.smsNotifications}
            onChange={(value) => handleSettingChange('smsNotifications', value)}
            label="Notifications SMS"
            description="Recevez des notifications par SMS (des frais peuvent s'appliquer)"
          />
          <ToggleSwitch
            enabled={settings.marketingEmails}
            onChange={(value) => handleSettingChange('marketingEmails', value)}
            label="Emails marketing"
            description="Recevez des informations sur nos nouveaux produits et offres"
          />
        </div>
      </SettingSection>

      {/* Sécurité */}
      <SettingSection title="Sécurité" icon={Shield}>
        <div className="space-y-1">
          <ToggleSwitch
            enabled={settings.twoFactorAuth}
            onChange={(value) => handleSettingChange('twoFactorAuth', value)}
            label="Authentification à deux facteurs"
            description="Ajoutez une couche de sécurité supplémentaire à votre compte"
          />
          <ToggleSwitch
            enabled={settings.loginAlerts}
            onChange={(value) => handleSettingChange('loginAlerts', value)}
            label="Alertes de connexion"
            description="Recevez une notification lors de chaque connexion à votre compte"
          />
        </div>
        
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 mr-3" />
            <div>
              <h4 className="text-sm font-medium text-amber-800">Sécurité du compte</h4>
              <p className="text-sm text-amber-700 mt-1">
                Pour modifier votre mot de passe ou activer l'authentification à deux facteurs, 
                contactez notre support technique.
              </p>
            </div>
          </div>
        </div>
      </SettingSection>



      {/* Bouton de sauvegarde */}
      <div className="flex justify-center sm:justify-end">
        <button
          onClick={handleSave}
          disabled={loading}
          className="flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 text-sm sm:text-base w-full sm:w-auto justify-center"
        >
          {loading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          <span className="hidden sm:inline">Sauvegarder les paramètres</span>
          <span className="sm:hidden">Sauvegarder</span>
        </button>
      </div>
    </div>
  );
};

export default UserSettings;